import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Newspaper, CalendarBlank, ArrowRight } from '@phosphor-icons/react';
import { setPageMeta } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = {
  homeowner: 'bg-[#2B4C6F]',
  contractor: 'bg-[#D95800]',
  tools: 'bg-emerald-700',
};

const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/blog/posts${category ? `?category=${category}` : ''}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ posts: [], categories: [] }))
      .finally(() => setLoading(false));
    setPageMeta({
      title: 'Construction Blog — Tips for Homeowners, Contractors & Pros | ConstructHelp',
      description: 'Weekly expert articles on hiring contractors, growing a construction business, and buying tools & materials smartly. Written for real-world results.',
      url: `${window.location.origin}/blog`,
    });
    window.scrollTo(0, 0);
  }, [category]);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="blog-page">
      {/* Hero */}
      <div className="bg-[#0F172A] border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 mb-3">
            <Newspaper size={20} weight="bold" className="text-[#D95800]" />
            <span className="text-[10px] font-black uppercase tracking-widest text-[#D95800]">The ConstructHelp Blog</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight text-white leading-tight">
            Build Smarter,<br />Every Week
          </h1>
          <p className="text-neutral-400 mt-3 max-w-xl text-sm md:text-base">
            Practical guides for homeowners, contractors and tradespeople — a new expert article published every week.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Category filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="blog-category-filter">
          <button
            onClick={() => setSearchParams({})}
            className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full ${!category ? 'bg-black text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
            data-testid="blog-filter-all"
          >
            All
          </button>
          {(data?.categories || []).map((c) => (
            <button
              key={c.key}
              onClick={() => setSearchParams({ category: c.key })}
              className={`text-xs font-black uppercase px-3 py-1.5 border-2 border-black rounded-full ${category === c.key ? 'bg-black text-white' : 'bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300'}`}
              data-testid={`blog-filter-${c.key}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin w-10 h-10 border-4 border-[#D95800] border-t-transparent rounded-full" /></div>
        ) : (data?.posts || []).length === 0 ? (
          <div className="text-center py-20" data-testid="blog-empty">
            <Newspaper size={56} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
            <p className="font-black uppercase text-neutral-700 dark:text-neutral-300">First article coming soon</p>
            <p className="text-sm text-neutral-500 mt-1">Our weekly publishing agent is warming up.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="blog-post-grid">
            {data.posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group bg-white dark:bg-neutral-800 border-2 border-black rounded-xl p-5 shadow-[4px_4px_0px_#000] hover:-translate-y-1 transition-transform"
                data-testid={`blog-card-${p.slug}`}
              >
                {p.cover_image && (
                  <img
                    src={`${API_URL}${p.cover_image}`}
                    alt={p.title}
                    loading="lazy"
                    className="w-full aspect-video object-cover border-2 border-black rounded-lg mb-3"
                    data-testid={`blog-card-cover-${p.slug}`}
                  />
                )}
                <div className="flex items-center gap-2 mb-2">
                  <span className={`${CATEGORY_COLORS[p.category] || 'bg-neutral-700'} text-white text-[10px] font-black uppercase px-2 py-0.5 border border-black rounded`}>
                    {p.category_label}
                  </span>
                  <span className="text-[11px] text-neutral-400 flex items-center gap-1">
                    <CalendarBlank size={12} weight="bold" /> {fmtDate(p.created_at)}
                  </span>
                </div>
                <h2 className="text-lg font-black text-neutral-900 dark:text-white leading-snug group-hover:text-[#D95800] transition-colors">
                  {p.title}
                </h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 line-clamp-3">{p.meta_description}</p>
                <span className="inline-flex items-center gap-1 text-xs font-black uppercase text-[#D95800] mt-3">
                  Read article <ArrowRight size={13} weight="bold" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
